import {
  Button,
  Card,
  Col,
  Create,
  CreateButton,
  Divider,
  Form,
  Icons,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  UploadFile,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { SingleDatePicker } from 'components/date-picker/SingleDatePicker';
import {
  AddDamageProductDrawer,
  EditDamageProductDrawer,
  ModuleExpenseDrawer,
} from 'components/drawer';
import dayjs, { Dayjs } from 'dayjs';
import { IDamageGood } from 'interfaces';
import { useEffect, useReducer, useState } from 'react';
import { createDamage } from 'services/damage';
import { ICreateExpenseReturnType } from 'services/expense';
import { deleteExpenseCategory } from 'services/expense-category';
import { IVariant, getVariantsDropdown } from 'services/variant';
import { getWarehouses } from 'services/warehouse';
import { downloadCSVFile, readCSV } from 'utilities/file';
import { findDuplicate } from 'utilities/shared';
import styles from './style.module.css';

export type IDamagedProduct = IVariant & {
  quantity: number;
  lossAmount: number;
};

export interface IOption {
  id: string;
  name: string;
}

interface IState {
  damagedProducts: IDamagedProduct[];
  selectedWarehouse: string | undefined;
}

enum ActionKind {
  SetDamagedProducts = 'SET_DAMAGED_PRODUCTS',
  AddDamagedProduct = 'ADD_DAMAGED_PRODUCT',
  RemoveDamagedProduct = 'REMOVE_DAMAGED_PRODUCT',
  EditDamagedProduct = 'EDI_DAMAGED_PRODUCT',
  SetWarehouse = 'SET_WAREHOUSE',
}

type Action =
  | { type: ActionKind.SetDamagedProducts; payload: IDamagedProduct[] }
  | { type: ActionKind.AddDamagedProduct; payload: IDamagedProduct }
  | { type: ActionKind.RemoveDamagedProduct; payload: string }
  | { type: ActionKind.EditDamagedProduct; payload: IDamagedProduct }
  | { type: ActionKind.SetWarehouse; payload: string };

const initialState: IState = {
  damagedProducts: [],
  selectedWarehouse: undefined,
};

const reducer = (state: IState, action: Action) => {
  switch (action.type) {
    case ActionKind.SetDamagedProducts:
      return { ...state, damagedProducts: action.payload };
    case ActionKind.AddDamagedProduct:
      const isProductExist = state.damagedProducts.find(
        (product) => product.productVariantId === action.payload.productVariantId,
      );
      if (isProductExist) {
        return state;
      }
      return { ...state, damagedProducts: [...state.damagedProducts, action.payload] };
    case ActionKind.RemoveDamagedProduct:
      const damagedProducts = state.damagedProducts.filter(
        (product) => product.productVariantId !== action.payload,
      );
      return { ...state, damagedProducts };
    case ActionKind.EditDamagedProduct:
      const modifiedProducts = state.damagedProducts.map((product) =>
        product.productVariantId === action.payload.productVariantId
          ? { ...action.payload }
          : { ...product },
      );
      return { ...state, damagedProducts: modifiedProducts };
    case ActionKind.SetWarehouse:
      return { ...state, selectedWarehouse: action.payload };
    default:
      return state;
  }
};

const { Option } = Select;
const { Text, Title } = Typography;

export const DamageCreate: React.FC = () => {
  const [warehouses, setWarehouses] = useState<IOption[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [damageDate, setDamageDate] = useState<Date>();
  const [isExpenseVisible, setIsExpenseVisible] = useState(false);
  const [expenses, setExpenses] = useState<ICreateExpenseReturnType[]>([]);
  const [{ damagedProducts, selectedWarehouse }, dispatch] = useReducer(reducer, initialState);

  const { list } = useNavigation();
  const { formProps, saveButtonProps, queryResult } = useForm<IDamageGood>({});

  useEffect(() => {
    (async () => {
      setIsFetching(true);
      try {
        const warehousesResponse = await getWarehouses();
        const warehousesData = warehousesResponse.data.map((warehouse) => ({
          id: warehouse.id,
          name: warehouse.name,
        }));
        setWarehouses(warehousesData);
      } catch (err: any) {
        setWarehouses([]);
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsFetching(false);
    })();
  }, []);

  const addExpenses = (createdExpense: ICreateExpenseReturnType) => {
    setExpenses((prevExpenses) => {
      return [...prevExpenses, createdExpense];
    });
  };

  const getOptions = (items: IOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;
    dispatch({ type: ActionKind.SetWarehouse, payload: warehouseId });
  };

  const onFinish = async (values: any) => {
    setIsLoading(true);
    try {
      const damageGoodsData = {
        damageDate: values.damageDate ? new Date(values.damageDate) : new Date(),
        damageDetails: values.damageDetails as string,
        warehouse: values.warehouse as string,
        damageProducts: damagedProducts.map((product) => ({
          productVariant: product.productVariantId,
          quantity: product.quantity,
          lossAmount: Number.parseFloat(product.lossAmount.toFixed(2)),
        })),
        damageExpense: expenses.map((expense) => ({
          id: expense.id,
        })),
      };

      await createDamage(damageGoodsData);
      notification.success({
        message: 'Success',
        description: 'Successfully created a damage.',
      });
      list('damages');
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const renderedExpenses = expenses.map((expense) => {
    const paymentMethodBankName = expense.paymentMethod ? `-${expense.paymentMethod.bankName}` : '';
    const tagString = `${expense.category.name}${paymentMethodBankName}-${expense.amount}`;

    return (
      <Tag
        key={expense.id}
        closable
        onClose={() => {
          try {
            deleteExpenseCategory(expense.id);
            setExpenses((prevExpenses) => {
              return prevExpenses.filter((item) => item.id !== expense.id);
            });
          } catch (err: any) {
            notification.error({
              message: 'Failed',
              description: err.message,
            });
          }
        }}
      >
        {tagString}
      </Tag>
    );
  });

  return (
    <>
      <ModuleExpenseDrawer
        onClose={() => setIsExpenseVisible(false)}
        visible={isExpenseVisible}
        addExpenses={addExpenses}
        title="Add Expense"
      />

      <Create
        isLoading={queryResult?.isFetching}
        saveButtonProps={{
          ...saveButtonProps,
          disabled: !damagedProducts.length,
          loading: isLoading,
        }}
      >
        <Form
          {...formProps}
          layout="vertical"
          initialValues={{
            isActive: true,
          }}
          onFinish={onFinish}
        >
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <Card>
                <Form.Item
                  label="Warehouse"
                  name="warehouse"
                  rules={[{ required: true, message: 'Please select a warehouse' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select a warehouse"
                    loading={isFetching}
                    disabled={isFetching}
                    onSelect={(value: string) => onSelectWarehouse(value?.toString())}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(warehouses)}
                  </Select>
                </Form.Item>
                <Form.Item label="Date" name="damageDate">
                  <SingleDatePicker
                    value={dayjs(damageDate)}
                    onChange={(value: Dayjs) => setDamageDate(new Date(value.toDate()))}
                  />
                </Form.Item>
                <Form.Item label="Note" name="damageDetails">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button onClick={() => setIsExpenseVisible(true)} size="large">
                  Add Expense
                </Button>
              </div>
            </Col>
          </Row>

          <Row justify="center" style={{ marginTop: '1rem' }}>
            <Col span={12}>
              <Space size={[8, 16]} wrap>
                {renderedExpenses}
              </Space>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <ProductsTable
                damagedProducts={damagedProducts}
                warehouse={selectedWarehouse}
                damagedProductDispatcher={dispatch}
              />
            </Col>
          </Row>
        </Form>
      </Create>
    </>
  );
};

type ProductsTableProps = {
  damagedProducts: IDamagedProduct[];
  warehouse?: string;
  damagedProductDispatcher: React.Dispatch<Action>;
};

const ProductsTable: React.FC<ProductsTableProps> = (props) => {
  const [editDamagedProduct, setEditDamagedProduct] = useState<IDamagedProduct | null>(null);
  const [isAddDrawerVisible, setIsAddDrawerVisible] = useState<boolean>(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState<boolean>(false);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const onSubmitAddNewProduct = (product: IDamagedProduct) => {
    props.damagedProductDispatcher({ type: ActionKind.AddDamagedProduct, payload: product });
    setIsAddDrawerVisible(false);
  };

  const handleSampleCsv = () => {
    const rows = [['sku', 'productVariantName', 'quantity', 'lossAmount']];
    const result = [
      { sku: 'P0001', productVariantName: 'Product 1', quantity: '100', lossAmount: '150' },
      { sku: 'P0002', productVariantName: 'Product 2', quantity: '150', lossAmount: '200' },
      { sku: 'P0003', productVariantName: 'Product 3', quantity: '200', lossAmount: '250' },
    ];
    result.forEach((elem) => {
      rows.push([elem.sku, elem.productVariantName, elem.quantity, elem.lossAmount]);
    });

    downloadCSVFile(rows);
  };

  const handleFileUpload = async (info: UploadChangeParam<UploadFile<any>>) => {
    if (!info.fileList.length) return;

    setIsImporting(true);
    try {
      interface IParsedData {
        id: string;
        sku: string;
        productVariantName: string;
        quantity: number;
        lossAmount: number;
      }

      let rowNumber = 0;
      const file = info.fileList[0].originFileObj!;
      const data: IParsedData[] = await readCSV({
        file,
        transformFunction: (row: any) => {
          rowNumber += 1;

          if (!row.sku) {
            throw new Error(`'sku' has missing values in row ${rowNumber}`);
          }

          if (!row.productVariantName) {
            throw new Error(`'productVariantName' has missing values in row ${rowNumber}`);
          }

          if (!row.quantity || isNaN(Number(row.quantity))) {
            throw new Error(`'quantity' is not a number in row ${rowNumber}`);
          }

          if (!row.lossAmount || isNaN(Number(row.lossAmount))) {
            throw new Error(`'lossAmount' is not a number in row ${rowNumber}`);
          }

          return {
            id: row.sku,
            sku: row.sku,
            productVariantName: row.productVariantName,
            quantity: Number(row.quantity),
            lossAmount: Number.parseFloat(Number(row.lossAmount).toFixed(2)),
          };
        },
        validateFunction: (row: any) => {
          const requiredFields = ['sku', 'productVariantName', 'quantity', 'lossAmount'];
          const keys = Object.keys(row);
          return requiredFields.every((field) => keys.includes(field));
        },
      });

      const mergedData = data.reduce((previousValue, currentValue) => {
        const { productVariantName, sku, quantity } = currentValue;
        const existingProduct = previousValue.find(
          (item: any) => item.sku === sku && item.productVariantName === productVariantName,
        );
        if (existingProduct) {
          existingProduct.quantity += quantity;
          return previousValue;
        }

        previousValue.push(currentValue);
        return previousValue;
      }, [] as IParsedData[]);

      const duplicateSku = findDuplicate(mergedData, 'sku');
      const uniqueDuplicates = Array.from(new Set(duplicateSku));
      if (duplicateSku.length) {
        notification.error({
          message: 'Failed',
          description: `Found ${
            duplicateSku.length
          } duplicate values for 'sku' - ${uniqueDuplicates.join(', ')}`,
        });
        return;
      }

      const skus = mergedData.map((item) => item.sku);
      const variants = await getVariantsDropdown({
        sku: skus,
      });
      if (mergedData.length !== variants.length) {
        const variantSkus = variants.map((item) => item.sku);
        const notExists = skus.filter((sku) => !variantSkus.includes(sku));
        notification.warn({
          message: 'Warning',
          description: `Product "${notExists.join(', ')}" not exists.`,
        });
      }
      const damagedProducts = variants.map((variant) => {
        const data = mergedData.find((item) => item.sku === variant.sku);
        return {
          ...variant,
          lossAmount: data!.lossAmount,
          quantity: data!.quantity,
        };
      });

      props.damagedProductDispatcher({
        type: ActionKind.SetDamagedProducts,
        payload: damagedProducts,
      });

      // props.damageGoodDispatcher({ type: ActionKind.SetDamagedProducts, payload: mergedData });
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsImporting(false);
  };

  const cardTitle = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        width: '100%',
        whiteSpace: 'normal',
      }}
    >
      <Title level={4} style={{ margin: 0 }}>
        Add product
      </Title>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <CreateButton type="primary" onClick={() => setIsAddDrawerVisible(true)}>
          Add New
        </CreateButton>
        <Button onClick={handleSampleCsv} icon={<Icons.DownloadOutlined />}>
          Sample CSV
        </Button>
        <Upload
          accept="text/csv"
          maxCount={1}
          beforeUpload={() => false}
          onChange={handleFileUpload}
          onRemove={() =>
            props.damagedProductDispatcher({ type: ActionKind.SetDamagedProducts, payload: [] })
          }
        >
          <Button icon={<Icons.ImportOutlined />} loading={isImporting}>
            Import
          </Button>
        </Upload>
      </div>
    </div>
  );

  const subTotal = props.damagedProducts.reduce((total, product) => {
    total += Number.parseFloat(product.lossAmount.toFixed(2));
    return total;
  }, 0);

  const footer = (
    <div className={styles['product-footer']}>
      <Text>Sub-Total: {subTotal}</Text>
    </div>
  );

  return (
    <>
      {/* Add Drawer Form */}
      <AddDamageProductDrawer
        visible={isAddDrawerVisible}
        damagedProducts={props.damagedProducts}
        warehouseId={props.warehouse}
        onClose={() => setIsAddDrawerVisible(false)}
        onSubmit={onSubmitAddNewProduct}
      />

      {/* Edit Drawer Form */}
      {editDamagedProduct && (
        <EditDamageProductDrawer
          visible={isEditDrawerVisible}
          editProduct={editDamagedProduct}
          warehouse={props.warehouse}
          onClose={() => setIsEditDrawerVisible(false)}
          onSubmit={(product) => {
            props.damagedProductDispatcher({
              type: ActionKind.EditDamagedProduct,
              payload: product,
            });
            setIsEditDrawerVisible(false);
          }}
        />
      )}

      <Card title={cardTitle}>
        <Table
          dataSource={props.damagedProducts}
          rowKey="sku"
          scroll={{ x: true }}
          footer={(_data) => footer}
        >
          <Table.Column dataIndex="sku" title="SKU" />
          <Table.Column dataIndex="productVariantName" title="Variant Name" />
          <Table.Column dataIndex="quantity" title="Quantity" />
          <Table.Column dataIndex="lossAmount" title="Loss Amount" />
          <Table.Column<IDamagedProduct>
            title="Actions"
            dataIndex="actions"
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <Button
                    size="small"
                    icon={<Icons.EditOutlined />}
                    onClick={() => {
                      setIsEditDrawerVisible(true);
                      setEditDamagedProduct(record);
                    }}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<Icons.DeleteOutlined />}
                    onClick={() =>
                      props.damagedProductDispatcher({
                        type: ActionKind.RemoveDamagedProduct,
                        payload: record.productVariantId,
                      })
                    }
                  />
                </Space>
              );
            }}
          />
        </Table>
      </Card>
    </>
  );
};
